import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useAppContext } from "@/shared/hooks/useAppContext";
import { useDemoMode } from "@/pages/demo/DemoModeContext";
import { DocumentExtractionStatus } from "@/shared/types/documentTypes";
import type { DocumentType, SmartSummary, DocumentExtraction } from "@/shared/types/documentTypes";
import { isExtractionReadable, type DocumentViewMetadata } from "@/shared/utils/documentUtils";
import { getAccessToken } from "@/shared/types/auth";
import { useEffect, useCallback } from "react";
import { supabase } from "@/shared/lib/supabase";

function useResolvedPersonId(providedPersonId?: number) {
  const { person } = useAppContext();
  return providedPersonId ?? person?.estate_id;
}

export function useDocumentMetadata({
  documentId,
  estateId: providedPersonId,
}: {
  documentId: number;
  estateId?: number;
}) {
  const estateId = useResolvedPersonId(providedPersonId);
  const queryClient = useQueryClient();
  const { user } = useAppContext();
  const { isDemoMode } = useDemoMode();

  useEffect(() => {
    if (isDemoMode) return;

    const handleSummaryUpdate = () => {
      queryClient.refetchQueries({
        queryKey: ["documentMetadata", documentId, estateId],
      });
    };

    window.addEventListener("document-summary-updated", handleSummaryUpdate);
    return () => {
      window.removeEventListener("document-summary-updated", handleSummaryUpdate);
    };
  }, [documentId, estateId, queryClient, isDemoMode]);

  useEffect(() => {
    if (isDemoMode || !user?.user_id || !documentId) return;

    const channel = supabase
      .channel(`doc-notif-${documentId}-${user.user_id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "estate_notifications",
          filter: `entity_id=eq.${documentId}`,
        },
        (payload) => {
          if (payload.new["notification_type"] === "document_processed") {
            queryClient.refetchQueries({
              queryKey: ["documentMetadata", documentId, estateId],
            });
          }
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [documentId, user?.user_id, estateId, queryClient, isDemoMode]);

  const { data, isLoading, error } = useQuery<DocumentViewMetadata>({
    queryKey: ["documentMetadata", documentId, estateId],
    queryFn: async () => {
      if (!estateId) throw new Error("Person ID is required");

      const accessToken = await getAccessToken();
      if (!accessToken) throw new Error("Authentication required");

      const response = await fetch(
        `${
          import.meta.env.VITE_SUPABASE_URL
        }/functions/v1/documents/${estateId}/document/${documentId}/metadata`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch document: ${response.status}`);
      }

      const json = await response.json();
      if (!json.document) throw new Error("Document not found");

      const doc = json.document;
      return {
        id: doc.id as number,
        extractionStatus: doc.extraction_status as string,
        documentType: doc.document_type as DocumentType,
        fileName: doc.file_name as string,
        displayName: (doc.display_name as string | null) || null,
        smartSummary: (doc.smart_summary as SmartSummary | null) || null,
        documentExtraction: (doc.document_extraction as DocumentExtraction | null) || null,
        specificDocExtraction:
          (doc.specific_doc_extraction as Record<string, unknown> | null) || null,
        specificDocCitations:
          (doc.specific_doc_citations as Record<string, unknown> | null) || null,
        createdAt: doc.created_at as string,
      };
    },
    enabled: !!estateId && !!documentId && !isDemoMode,
    staleTime: isDemoMode ? Infinity : 5 * 60 * 1000,
  });

  return { metadata: data ?? null, isLoading, error };
}

export function useDocumentUrl({
  documentId,
  estateId: providedPersonId,
}: {
  documentId: number;
  estateId?: number;
}) {
  const estateId = useResolvedPersonId(providedPersonId);
  const { isDemoMode } = useDemoMode();

  const { data, isLoading, error } = useQuery({
    queryKey: ["documentUrl", documentId, estateId],
    queryFn: async () => {
      if (!estateId) throw new Error("Person ID is required");

      const accessToken = await getAccessToken();
      if (!accessToken) throw new Error("Authentication required");

      const response = await fetch(
        `${
          import.meta.env.VITE_SUPABASE_URL
        }/functions/v1/documents/${estateId}/document/${documentId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
        },
      );

      if (!response.ok) throw new Error("Failed to get document URL");

      const urlData = await response.json();
      const presignedUrl = urlData.downloadUrl as string;

      try {
        const fileResponse = await fetch(presignedUrl);
        if (fileResponse.ok) {
          const blob = await fileResponse.blob();
          return URL.createObjectURL(blob);
        }
      } catch {
        // Fall back to presigned URL if prefetch fails
      }

      return presignedUrl;
    },
    enabled: !!estateId && !!documentId && !isDemoMode,
    staleTime: isDemoMode ? Infinity : 90 * 1000,
  });

  return { previewUrl: data ?? null, isLoading, error };
}

export function useInvalidateDocumentUrl({
  documentId,
  estateId: providedPersonId,
}: {
  documentId: number;
  estateId?: number;
}) {
  const estateId = useResolvedPersonId(providedPersonId);
  const queryClient = useQueryClient();

  return useCallback(async () => {
    await queryClient.refetchQueries({
      queryKey: ["documentUrl", documentId, estateId],
    });
  }, [queryClient, documentId, estateId]);
}

export function useDocumentExtractionPoller({
  documentId,
  extractionStatus,
  smartSummaryStatus,
  hasSpecificDocData,
}: {
  documentId: number;
  extractionStatus: string | undefined;
  smartSummaryStatus: string | undefined;
  hasSpecificDocData: boolean;
}) {
  const { isDemoMode } = useDemoMode();
  const allDone =
    extractionStatus === DocumentExtractionStatus.ExtractionComplete ||
    extractionStatus === DocumentExtractionStatus.ExtractionFailed ||
    extractionStatus === DocumentExtractionStatus.ExtractionPartial ||
    isExtractionReadable(smartSummaryStatus, hasSpecificDocData);

  useEffect(() => {
    if (!documentId || allDone || isDemoMode) return;

    let pollInterval: NodeJS.Timeout | null = null;
    let isMounted = true;
    let attempts = 0;

    const poll = async () => {
      if (!isMounted) return;

      attempts++;
      if (attempts > 60) {
        if (pollInterval) clearInterval(pollInterval);
        return;
      }

      try {
        const { data, error } = await supabase
          .from("estate_documents")
          .select(
            "extraction_status, smart_summary, specific_doc_extraction, specific_doc_citations, duplicate_id",
          )
          .eq("id", documentId)
          .single();

        if (!isMounted || error) return;

        let checkData = data;
        if (data.duplicate_id && (!data.smart_summary || !data.specific_doc_extraction)) {
          const { data: original } = await supabase
            .from("estate_documents")
            .select("extraction_status, smart_summary, specific_doc_extraction")
            .eq("id", data.duplicate_id)
            .single();
          if (original) {
            checkData = {
              extraction_status:
                data.extraction_status === "pending"
                  ? original.extraction_status
                  : data.extraction_status,
              smart_summary: data.smart_summary || original.smart_summary,
              specific_doc_extraction:
                data.specific_doc_extraction || original.specific_doc_extraction,
              specific_doc_citations: data.specific_doc_citations,
              duplicate_id: data.duplicate_id,
            };
          }
        }

        const extractionStopped = ["completed", "failed", "partial"].includes(
          checkData.extraction_status,
        );
        const readable = isExtractionReadable(
          checkData.smart_summary?.status,
          !!checkData.specific_doc_extraction,
        );

        if (extractionStopped || readable) {
          window.dispatchEvent(new CustomEvent("document-summary-updated"));
          if (pollInterval) clearInterval(pollInterval);
        }
      } catch {
        // Ignore polling errors
      }
    };

    poll();
    pollInterval = setInterval(poll, 2000);

    return () => {
      isMounted = false;
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [documentId, allDone, isDemoMode]);
}
